// client/src/services/lexistarRoutes.js
//
// LexiStar routes & lanes API client (ST-00188 M2-A1; contracts m2 §4.1).
// Server: server/modules/lexistar/routing (/api/lexistar/lanes, /routes,
// /routes:generate, /routes:fromTrip, /advisories, /toll-plazas,
// /reason-codes).
//
// Thin wrapper over the shared axios instance in `services/api` so the page
// inherits the cookie-session auth interceptors, CSRF header and single-flight
// 401 refresh. `api` already carries `/api` in its baseURL. Every helper
// resolves to the response BODY. Payloads use migration 023's column names
// (snake_case); geometry is GeoJSON [lon, lat].

import api from './api';

const BASE = '/lexistar';

/** Drop undefined / null / '' so the API never sees `q=` or `lane_id=`. */
const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );

const body = (res) => res.data;

// ── Lanes ────────────────────────────────────────────────────────────────────

/** { lanes: [{ id, from_location_id, to_location_id, from_location_name, …, access_type, route_count }] } */
export const listLanes = (params) => api.get(`${BASE}/lanes`, { params: clean(params) }).then(body);

/** { lane } — 409 { existingLaneId } when the directional lane exists. */
export const createLane = (data) => api.post(`${BASE}/lanes`, data).then(body);

// ── Routes ───────────────────────────────────────────────────────────────────

/** { routes: [{ id, code, name, status, current_version, planned_km, …, is_primary, access_type }] } */
export const listRoutes = (params) =>
  api.get(`${BASE}/routes`, { params: clean(params) }).then(body);

/** { route, lane, current_version, versions, advisories, can_change } */
export const getRoute = (id) => api.get(`${BASE}/routes/${id}`).then(body);

/**
 * Create a route on a lane (lane_id, or from_location_id + to_location_id to
 * find-or-create the lane), optionally with its first version in `version`.
 * Resolves to the same body as getRoute.
 */
export const createRoute = (data) => api.post(`${BASE}/routes`, data).then(body);

/** Rename or change status ('approved' | 'proposed' | 'retired'). */
export const updateRoute = (id, data) => api.patch(`${BASE}/routes/${id}`, data).then(body);

/** New immutable version; omitted fields are copied from the current one. */
export const createVersion = (id, data) =>
  api.post(`${BASE}/routes/${id}/versions`, data).then(body);

/** { version } with geometry */
export const getVersion = (id, v) => api.get(`${BASE}/routes/${id}/versions/${v}`).then(body);

/** GeoJSON Feature (Polygon) of the version's corridor; `width` previews another width. */
export const getCorridor = (id, v, width) =>
  api.get(`${BASE}/routes/${id}/versions/${v}/corridor`, { params: clean({ width }) }).then(body);

/**
 * Engine-generated proposal (nothing saved):
 * { geometry, planned_km, planned_min, legs, engine: 'valhalla'|'fake', engine_note, from, to, via }
 * @param {{from_location_id?:number,to_location_id?:number,from_point?:number[],to_point?:number[],via?:number[][],vehicle?:object}} data
 */
export const generateRoute = (data) => api.post(`${BASE}/routes:generate`, data).then(body);

/**
 * Golden run from a finished trip ({ trip_id }) or from one vehicle's stored
 * positions ({ registration | asset_id, from, to }); nothing saved:
 * { geometry, planned_km, planned_min, engine, points_total, window, trip, source_trip_id }
 */
export const routeFromTrip = (data) => api.post(`${BASE}/routes:fromTrip`, data).then(body);

// ── Advisories ──────────────────────────────────────────────────────────────

/** { advisories } — state: 'current' (default) | 'active' | 'expired' | 'retracted' | 'all' */
export const listAdvisories = (params) =>
  api.get(`${BASE}/advisories`, { params: clean(params) }).then(body);

/** { advisory } */
export const createAdvisory = (routeId, data) =>
  api.post(`${BASE}/routes/${routeId}/advisories`, data).then(body);

/** { advisory } */
export const retractAdvisory = (id) => api.post(`${BASE}/advisories/${id}:retract`).then(body);

// ── Toll plazas and reason codes ───────────────────────────────────────────

/** { toll_plazas } — the shared library plus your own. */
export const listTollPlazas = () => api.get(`${BASE}/toll-plazas`).then(body);

/** { reason_codes } — seeded with the defaults on first use. */
export const listReasonCodes = (params) =>
  api.get(`${BASE}/reason-codes`, { params: clean(params) }).then(body);

export const createReasonCode = (data) => api.post(`${BASE}/reason-codes`, data).then(body);

export const updateReasonCode = (id, data) =>
  api.patch(`${BASE}/reason-codes/${id}`, data).then(body);

// ── Reference data the editor needs from other modules ────────────────────

/** Active sites (order-management locations): [{ location_id, location_name, … }] */
export const listSites = () =>
  api.get('/locations', { params: { active: 'true' } }).then((res) => res.data?.locations || []);

/** The organisation's geofences (versioned columns when migration 021 is applied). */
export const listGeofences = () => api.get('/geofences').then((res) => res.data?.data || []);

/**
 * Finished LexiStar trips for the golden-run picker (M2-A2's GET /lexistar/trips).
 * Resolves to [] when trips are not available on this server.
 */
export const listFinishedTrips = () =>
  api
    .get(`${BASE}/trips`, { params: { stage: 'COMPLETED,RECONCILED,CLOSED', limit: 100 } })
    .then((res) => res.data?.trips || [])
    .catch(() => []);
